package com.tixforgigs.common.core.util

import com.tixforgigs.apax.models.Country
import com.tixforgigs.common.core.extensions.orElse
import com.tixforgigs.common.core.models.ui.InputState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mUserLocaleState
import com.tixforgigs.common.core.util.StorageUtil.getStorageItem
import com.tixforgigs.common.core.util.StorageUtil.setStorageItem
import com.tixforgigs.common.http.ApiGetPath
import com.tixforgigs.common.http.HttpClient.apiV4Get
import de.comahe.i18n4k.*
import de.comahe.i18n4k.config.I18n4kConfigDefault
import io.github.oshai.kotlinlogging.KotlinLogging
import io.github.oshai.kotlinlogging.logger
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.flow.updateAndGet
import kotlinx.coroutines.launch
import kotlinx.serialization.builtins.serializer

object LocalizationUtil {

  private val log by KotlinLogging.logger()
  private val i18n4kConfig = I18n4kConfigDefault()

  val mPaymentCountries = MutableStateFlow(
    value = DefaultPaymentCountries.entries.map { it.country }
  )

  val currentPaymentCountry: MutableStateFlow<InputState<Country>> = MutableStateFlow(
    InputState(
      DefaultPaymentCountries.DE.country,
      true
    )
  )

  private val languageISOCodeInUrl
    get() = window.location.pathname.split("/").firstOrNull {
      Regex("^[a-z]{2}$", RegexOption.IGNORE_CASE).matches(it)
    }?.let { languageString ->
      SupportedTranslationLanguages.entries.firstOrNull { it.locale.language == languageString }
    }

  val requestedLanguageISOCode: String
    get() = languageISOCodeInUrl?.locale?.language.orEmpty()

  val initialLanguage: SupportedTranslationLanguages
    get() = mUserLocaleState.value?.let {
      forLocaleTag(it).toSupportedLanguageOrNull
    }.orElse {
      languageISOCodeInUrl.orElse {
        systemLocale.toSupportedLanguageOrNull.orElse { SupportedTranslationLanguages.EN }
      }
    }

  // todo(PROTO-346) - reload language without app reload possible?
  val currentTranslationLocale = MutableStateFlow(
    value = initialLanguage.locale
  )

  val translationLocaleAsString: String
    get() = toLocaleTag(
      language = currentTranslationLocale.value.language,
      country = currentTranslationLocale.value.country,
      script = "",
      variant = "",
      separator = "-",
      extensions = null
    )

//  val Locale.toSupportedPaymentCountryOrNull: SupportedPaymentCountries?
//    get() = SupportedPaymentCountries.entries.firstOrNull { it.locale == this }

  val Locale.toSupportedLanguageOrNull: SupportedTranslationLanguages?
    get() = SupportedTranslationLanguages.entries.firstOrNull { it.locale == this }


  private fun initLocale() {
    i18n4k = i18n4kConfig
    i18n4kConfig.locale = currentTranslationLocale.value
  }

  fun changeLocale(locale: SupportedTranslationLanguages) {
    i18n4kConfig.locale = locale.locale
    setStorageItem(
      key = StorageUtil.StorageKey.UserLocale,
      item = locale.locale.toString(),
      serializer = String.serializer()
    )
    window.location.reload()
  }

  fun init() {
    initLocale()
    i18n4kInitCldrPluralRules()
    CoroutineScope(Dispatchers.Default).launch {
      getPaymentCountries()
    }
  }

  private suspend fun getPaymentCountries() {
    apiV4Get<List<Country>>(
      type = ApiGetPath.SystemCountries,
      onCompletionWithError = { _, _, _, _ ->
        mPaymentCountries.update { DefaultPaymentCountries.entries.map { it.country } }
      }
    )?.let { countries ->
      mPaymentCountries.updateAndGet { countries }.let { countries ->
        getStorageItem<String>(StorageUtil.StorageKey.PaymentCountryCode)?.let { countryCode ->
          countries.firstOrNull { it.countryCode == countryCode }?.let { country ->
            currentPaymentCountry.update {
              it.copy(
                inputValue = country,
                isValid = true
              )
            }
            return@let
          }
        }
      }
    }
  }
}

enum class SupportedTranslationLanguages(val locale: Locale) {
  DE(locale = DefaultLocaleImpl(language = "de", country = "DE")),
  EN(locale = DefaultLocaleImpl(language = "en", country = "US")),
  ES(locale = DefaultLocaleImpl(language = "es", country = "ES")),
}

enum class DefaultPaymentCountries(val country: Country) {
  DE(
    Country(
      countryCode = "DE",
      labelDe = "Deutschland",
      labelEn = "Germany",
      labelEs = "Alemania",
      labelNl = "Duitsland",
      currencyCode = "EUR",
      flagIcon = "\uD83C\uDDE9\uD83C\uDDEA",
      isPrioritized = true
    )
  ),
  AT(
    Country(
      countryCode = "AT",
      labelDe = "Österreich",
      labelEn = "Austria",
      labelEs = "Austria",
      labelNl = "Oostenrijk",
      currencyCode = "EUR",
      flagIcon = "\uD83C\uDDE6\uD83C\uDDF9",
      isPrioritized = true
    )
  ),
  NL(
    Country(
      countryCode = "NL",
      labelDe = "Niederlande",
      labelEn = "Netherlands",
      labelEs = "Países Bajos",
      labelNl = "Nederland",
      currencyCode = "EUR",
      flagIcon = "\uD83C\uDDF3\uD83C\uDDF1",
      isPrioritized = true
    )
  ),
  US(
    Country(
      countryCode = "US",
      labelDe = "USA",
      labelEn = "United States",
      labelEs = "Estados Unidos",
      labelNl = "Verenigde Staten",
      currencyCode = "USD",
      flagIcon = "\uD83C\uDDFA\uD83C\uDDF8",
      isPrioritized = false
    )
  )
}
